<template>
  <div class="dashboard-screen">
    <div
      class="screen-shell"
      :style="{ backgroundImage: `url(${backgroundImage})` }"
    >
      <img class="top-banner" :src="topBanner" alt="" />
      <header class="screen-header">
        <div class="header-meta">
          <span>{{ currentDate }}</span>
          <span class="meta-sep">{{ currentWeekday }}</span>
          <span>{{ currentTime }}</span>
        </div>

        <div class="header-actions">
          <button
            v-for="item in actions"
            :key="item"
            type="button"
            @click="handleAction(item)"
          >
            {{ item }}
          </button>
          <div class="date-range-filter">
            <button
              ref="dateRangeTrigger"
              type="button"
              class="date-range-trigger"
              :class="{ active: showDateRangePanel }"
              @click="toggleDateRangePanel"
            >
              {{ dateRangeLabel }}
            </button>
          </div>
          <button
            v-for="item in trailingActions"
            :key="item"
            type="button"
            @click="handleAction(item)"
          >
            {{ item }}
          </button>
        </div>
      </header>

      <div
        v-if="showDateRangePanel"
        class="date-range-panel"
        :class="{ 'is-expanded': dateMenuOpen, 'is-month-expanded': monthMenuOpen }"
        :style="dateRangePanelStyle"
      >
        <div class="date-range-quick-tools">
          <div class="date-range-month-control">
            <button
              type="button"
              class="date-range-month-trigger"
              :class="{ active: monthMenuOpen }"
              @click="toggleMonthMenu"
            >
              <span>{{ selectedQuickMonthLabel || '快捷选月' }}</span>
              <i class="date-range-month-arrow" :class="{ 'is-open': monthMenuOpen }"></i>
            </button>
            <div v-if="monthMenuOpen" class="date-range-month-menu">
              <button
                v-for="month in quickMonthOptions"
                :key="month.value"
                type="button"
                class="date-range-month-option"
                :class="{ active: selectedQuickMonth === month.value }"
                @click="selectQuickMonth(month.value)"
              >
                {{ month.label }}
              </button>
            </div>
          </div>
          <div class="date-range-shortcuts">
            <button
              v-for="shortcut in quickRangeOptions"
              :key="shortcut.text"
              type="button"
              class="date-range-shortcut"
              @click="applyQuickRange(shortcut.months)"
            >
              {{ shortcut.text }}
            </button>
          </div>
        </div>
        <div class="date-range-toolbar">
          <div class="date-range-date-control">
            <button type="button" class="date-range-date-trigger" @click="toggleDateMenu('start')">
              <span>{{ formatDraftDisplay('start') }}</span>
            </button>
            <div v-if="dateMenuOpen === 'start'" class="date-picker-panel">
              <div class="date-picker-row">
                <label class="date-picker-field">
                  <span>年</span>
                  <div class="date-picker-select">
                    <button type="button" class="date-picker-select-trigger" @click="toggleDateFieldMenu('start-year')">
                      <span>{{ dateDraft.start.year }}</span>
                      <i class="date-picker-select-arrow" :class="{ 'is-open': dateFieldMenuOpen === 'start-year' }"></i>
                    </button>
                    <div v-if="dateFieldMenuOpen === 'start-year'" class="date-picker-select-menu">
                      <button
                        v-for="year in yearOptions"
                        :key="`start-${year}`"
                        type="button"
                        class="date-picker-select-option"
                        :class="{ active: dateDraft.start.year === year }"
                        @click="selectDateFieldValue('start', 'year', year)"
                      >
                        {{ year }}
                      </button>
                    </div>
                  </div>
                </label>
                <label class="date-picker-field">
                  <span>月</span>
                  <div class="date-picker-select">
                    <button type="button" class="date-picker-select-trigger" @click="toggleDateFieldMenu('start-month')">
                      <span>{{ dateDraft.start.month }}</span>
                      <i class="date-picker-select-arrow" :class="{ 'is-open': dateFieldMenuOpen === 'start-month' }"></i>
                    </button>
                    <div v-if="dateFieldMenuOpen === 'start-month'" class="date-picker-select-menu">
                      <button
                        v-for="month in monthOptions"
                        :key="`start-${month}`"
                        type="button"
                        class="date-picker-select-option"
                        :class="{ active: dateDraft.start.month === month }"
                        @click="selectDateFieldValue('start', 'month', month)"
                      >
                        {{ month }}
                      </button>
                    </div>
                  </div>
                </label>
                <label class="date-picker-field">
                  <span>日</span>
                  <div class="date-picker-select">
                    <button type="button" class="date-picker-select-trigger" @click="toggleDateFieldMenu('start-day')">
                      <span>{{ dateDraft.start.day }}</span>
                      <i class="date-picker-select-arrow" :class="{ 'is-open': dateFieldMenuOpen === 'start-day' }"></i>
                    </button>
                    <div v-if="dateFieldMenuOpen === 'start-day'" class="date-picker-select-menu">
                      <button
                        v-for="day in startDayOptions"
                        :key="`start-${day}`"
                        type="button"
                        class="date-picker-select-option"
                        :class="{ active: dateDraft.start.day === day }"
                        @click="selectDateFieldValue('start', 'day', day)"
                      >
                        {{ day }}
                      </button>
                    </div>
                  </div>
                </label>
              </div>
              <div class="date-picker-actions">
                <button type="button" class="date-picker-action date-picker-action--ghost" @click="closeDateMenu">取消</button>
                <button type="button" class="date-picker-action" @click="closeDateMenu">确定</button>
              </div>
            </div>
          </div>
          <span class="date-range-sep">—</span>
          <div class="date-range-date-control">
            <button type="button" class="date-range-date-trigger" @click="toggleDateMenu('end')">
              <span>{{ formatDraftDisplay('end') }}</span>
            </button>
            <div v-if="dateMenuOpen === 'end'" class="date-picker-panel date-picker-panel--right">
              <div class="date-picker-row">
                <label class="date-picker-field">
                  <span>年</span>
                  <div class="date-picker-select">
                    <button type="button" class="date-picker-select-trigger" @click="toggleDateFieldMenu('end-year')">
                      <span>{{ dateDraft.end.year }}</span>
                      <i class="date-picker-select-arrow" :class="{ 'is-open': dateFieldMenuOpen === 'end-year' }"></i>
                    </button>
                    <div v-if="dateFieldMenuOpen === 'end-year'" class="date-picker-select-menu">
                      <button
                        v-for="year in yearOptions"
                        :key="`end-${year}`"
                        type="button"
                        class="date-picker-select-option"
                        :class="{ active: dateDraft.end.year === year }"
                        @click="selectDateFieldValue('end', 'year', year)"
                      >
                        {{ year }}
                      </button>
                    </div>
                  </div>
                </label>
                <label class="date-picker-field">
                  <span>月</span>
                  <div class="date-picker-select">
                    <button type="button" class="date-picker-select-trigger" @click="toggleDateFieldMenu('end-month')">
                      <span>{{ dateDraft.end.month }}</span>
                      <i class="date-picker-select-arrow" :class="{ 'is-open': dateFieldMenuOpen === 'end-month' }"></i>
                    </button>
                    <div v-if="dateFieldMenuOpen === 'end-month'" class="date-picker-select-menu">
                      <button
                        v-for="month in monthOptions"
                        :key="`end-${month}`"
                        type="button"
                        class="date-picker-select-option"
                        :class="{ active: dateDraft.end.month === month }"
                        @click="selectDateFieldValue('end', 'month', month)"
                      >
                        {{ month }}
                      </button>
                    </div>
                  </div>
                </label>
                <label class="date-picker-field">
                  <span>日</span>
                  <div class="date-picker-select">
                    <button type="button" class="date-picker-select-trigger" @click="toggleDateFieldMenu('end-day')">
                      <span>{{ dateDraft.end.day }}</span>
                      <i class="date-picker-select-arrow" :class="{ 'is-open': dateFieldMenuOpen === 'end-day' }"></i>
                    </button>
                    <div v-if="dateFieldMenuOpen === 'end-day'" class="date-picker-select-menu">
                      <button
                        v-for="day in endDayOptions"
                        :key="`end-${day}`"
                        type="button"
                        class="date-picker-select-option"
                        :class="{ active: dateDraft.end.day === day }"
                        @click="selectDateFieldValue('end', 'day', day)"
                      >
                        {{ day }}
                      </button>
                    </div>
                  </div>
                </label>
              </div>
              <div class="date-picker-actions">
                <button type="button" class="date-picker-action date-picker-action--ghost" @click="closeDateMenu">取消</button>
                <button type="button" class="date-picker-action" @click="closeDateMenu">确定</button>
              </div>
            </div>
          </div>
        </div>
        <div v-if="!dateMenuOpen" class="date-range-actions">
          <button type="button" class="date-range-action date-range-action--ghost" @click="closeDateRangePanel">关闭</button>
          <button type="button" class="date-range-action date-range-action--ghost" @click="resetDateRange">重置</button>
          <button type="button" class="date-range-action" @click="applyDateRange">确定</button>
        </div>
      </div>

      <main class="screen-body">
        <LeftPanel class="body-panel left-col" :period-key="periodKey" :date-range="dateRange" />
        <CenterPanel class="body-panel center-col" :period-key="periodKey" :date-range="dateRange" />
        <RightPanel
          class="body-panel right-col"
          :period-key="periodKey"
          :date-range="dateRange"
          @period-change="handlePeriodChange"
        />
      </main>

      <aside
        v-if="showFloatingNotice"
        ref="floatingNotice"
        class="floating-notice"
        @mouseenter="setFloatingNoticePaused(true)"
        @mouseleave="setFloatingNoticePaused(false)"
        @focusin="setFloatingNoticePaused(true)"
        @focusout="setFloatingNoticePaused(false)"
      >
        <button type="button" class="floating-notice__link" @click="openFloatingPage">
          <span>预警模块</span>
          <strong>查看预警处置</strong>
        </button>
        <button type="button" class="floating-notice__close" aria-label="关闭飘窗" @click="closeFloatingNotice">×</button>
      </aside>

      <StatisticsDialog :visible="showStatsDialog" @close="closeStatisticsDialog" />
    </div>
  </div>
</template>

<script>
import LeftPanel from '@/components/left/index.vue'
import CenterPanel from '@/components/center/index.vue'
import RightPanel from '@/components/right/index.vue'
import StatisticsDialog from '@/components/common/StatisticsDialog.vue'
import { getAdminUrl } from '@/api/datav'
import { clearAuthState } from '@/utils/auth'
import { getMonthDateRange, getMonthOptions, getRecentDateRange } from '@/utils/dateRange'

const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']

function formatInputDate(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function buildDefaultRange() {
  const end = new Date()
  const start = new Date(end)
  start.setMonth(start.getMonth() - 1)
  return {
    startDate: formatInputDate(start),
    endDate: formatInputDate(end)
  }
}

function parseDateValue(value) {
  const [year, month, day] = String(value || '').split('-').map(Number)
  const now = new Date()
  return {
    year: Number.isFinite(year) ? year : now.getFullYear(),
    month: Number.isFinite(month) ? month : now.getMonth() + 1,
    day: Number.isFinite(day) ? day : now.getDate()
  }
}

function getDayCount(year, month) {
  return new Date(year, month, 0).getDate()
}

function formatDraftDate(draft) {
  const maxDay = getDayCount(draft.year, draft.month)
  const day = Math.min(draft.day, maxDay)
  return formatInputDate(new Date(draft.year, draft.month - 1, day))
}

function formatDateLabel(value) {
  if (!value) {
    return ''
  }
  const parts = value.split('-')
  if (parts.length !== 3) {
    return value
  }
  return `${Number(parts[0])}/${Number(parts[1])}/${Number(parts[2])}`
}

export default {
  name: 'DashboardScreen',
  components: {
    LeftPanel,
    CenterPanel,
    RightPanel,
    StatisticsDialog
  },
  data() {
    const defaultRange = buildDefaultRange()
    return {
      now: new Date(),
      timer: null,
      periodKey: 'day',
      backgroundImage: require('@/assets/bj.png'),
      topBanner: require('@/assets/top.png'),
      actions: ['数据统计'],
      trailingActions: ['考勤', '退出', '后台'],
      showStatsDialog: false,
      showFloatingNotice: true,
      showDateRangePanel: false,
      dateMenuOpen: '',
      dateFieldMenuOpen: '',
      dateRangePanelStyle: {},
      monthMenuOpen: false,
      quickRangeOptions: [
        { text: '近一个月', months: 1 },
        { text: '近半年', months: 6 },
        { text: '近一年', months: 12 }
      ],
      dateRange: {
        startDate: '',
        endDate: ''
      },
      dateDraft: {
        start: parseDateValue(defaultRange.startDate),
        end: parseDateValue(defaultRange.endDate)
      }
    }
  },
  computed: {
    currentDate() {
      const year = this.now.getFullYear()
      const month = String(this.now.getMonth() + 1).padStart(2, '0')
      const day = String(this.now.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    currentWeekday() {
      return weekdays[this.now.getDay()]
    },
    currentTime() {
      const hours = String(this.now.getHours()).padStart(2, '0')
      const minutes = String(this.now.getMinutes()).padStart(2, '0')
      return `${hours}:${minutes}`
    },
    dateRangeLabel() {
      const start = formatDateLabel(this.dateRange.startDate)
      const end = formatDateLabel(this.dateRange.endDate)
      if (!start || !end) {
        return '时间范围'
      }
      return `${start} - ${end}`
    },
    yearOptions() {
      const currentYear = new Date().getFullYear()
      const years = []
      for (let year = currentYear - 5; year <= 2035; year++) {
        years.push(year)
      }
      return years
    },
    monthOptions() {
      return Array.from({ length: 12 }, (_, index) => index + 1)
    },
    startDayOptions() {
      return Array.from(
        { length: getDayCount(this.dateDraft.start.year, this.dateDraft.start.month) },
        (_, index) => index + 1
      )
    },
    endDayOptions() {
      return Array.from(
        { length: getDayCount(this.dateDraft.end.year, this.dateDraft.end.month) },
        (_, index) => index + 1
      )
    },
    quickMonthOptions() {
      return getMonthOptions(this.now, 36)
    },
    selectedQuickMonth() {
      const start = formatDraftDate(this.dateDraft.start)
      const end = formatDraftDate(this.dateDraft.end)
      if (!start || !end) {
        return ''
      }

      const month = start.slice(0, 7)
      const [monthStart, monthEnd] = getMonthDateRange(month)
      if (!monthStart || !monthEnd) {
        return ''
      }

      return start === formatInputDate(monthStart) && end === formatInputDate(monthEnd)
        ? month
        : ''
    },
    selectedQuickMonthLabel() {
      const selected = this.quickMonthOptions.find(item => item.value === this.selectedQuickMonth)
      return selected ? selected.label : ''
    }
  },
  mounted() {
    this.timer = window.setInterval(() => {
      this.now = new Date()
    }, 1000)
    window.addEventListener('resize', this.updateDateRangePanelPosition)
    this.$nextTick(this.startFloatingNotice)
  },
  beforeDestroy() {
    window.clearInterval(this.timer)
    window.cancelAnimationFrame(this.floatingFrameId)
    window.removeEventListener('resize', this.updateDateRangePanelPosition)
  },
  methods: {
    handleAction(item) {
      if (item === '数据统计') {
        this.showStatsDialog = true
        return
      }

      if (item === '退出') {
        this.handleLogout()
        return
      }

      if (item === '考勤') {
        this.$router.push('/application')
        return
      }

      if (item === '后台') {
        this.openAdmin()
      }
    },
    handlePeriodChange(periodKey) {
      this.periodKey = periodKey
    },
    toggleDateRangePanel() {
      const draftRange = this.dateRange.startDate && this.dateRange.endDate
        ? this.dateRange
        : buildDefaultRange()
      this.dateDraft = {
        start: parseDateValue(draftRange.startDate),
        end: parseDateValue(draftRange.endDate)
      }
      this.dateMenuOpen = ''
      this.dateFieldMenuOpen = ''
      this.monthMenuOpen = false
      this.showDateRangePanel = !this.showDateRangePanel
      if (this.showDateRangePanel) {
        this.$nextTick(this.updateDateRangePanelPosition)
      }
    },
    closeDateRangePanel() {
      this.showDateRangePanel = false
      this.dateMenuOpen = ''
      this.dateFieldMenuOpen = ''
      this.monthMenuOpen = false
    },
    applyDateRange() {
      const startDate = formatDraftDate(this.dateDraft.start)
      const endDate = formatDraftDate(this.dateDraft.end)
      if (!startDate || !endDate) {
        window.alert('请选择开始日期和结束日期')
        return
      }
      if (startDate > endDate) {
        window.alert('开始日期不能晚于结束日期')
        return
      }
      this.dateRange = { startDate, endDate }
      this.closeDateRangePanel()
    },
    resetDateRange() {
      const defaultRange = buildDefaultRange()
      this.dateDraft = {
        start: parseDateValue(defaultRange.startDate),
        end: parseDateValue(defaultRange.endDate)
      }
      this.dateRange = {
        startDate: '',
        endDate: ''
      }
      this.closeDateRangePanel()
    },
    toggleDateMenu(type) {
      this.dateFieldMenuOpen = ''
      this.monthMenuOpen = false
      this.dateMenuOpen = this.dateMenuOpen === type ? '' : type
      this.$nextTick(this.updateDateRangePanelPosition)
    },
    closeDateMenu() {
      this.dateMenuOpen = ''
      this.dateFieldMenuOpen = ''
      this.$nextTick(this.updateDateRangePanelPosition)
    },
    toggleDateFieldMenu(key) {
      this.dateFieldMenuOpen = this.dateFieldMenuOpen === key ? '' : key
    },
    toggleMonthMenu() {
      this.dateMenuOpen = ''
      this.dateFieldMenuOpen = ''
      this.monthMenuOpen = !this.monthMenuOpen
      this.$nextTick(this.updateDateRangePanelPosition)
    },
    selectQuickMonth(month) {
      const [start, end] = getMonthDateRange(month)
      if (!start || !end) {
        return
      }

      this.dateDraft = {
        start: parseDateValue(formatInputDate(start)),
        end: parseDateValue(formatInputDate(end))
      }
      this.monthMenuOpen = false
      this.$nextTick(this.updateDateRangePanelPosition)
    },
    applyQuickRange(months) {
      const [start, end] = getRecentDateRange(months, this.now)
      this.dateDraft = {
        start: parseDateValue(formatInputDate(start)),
        end: parseDateValue(formatInputDate(end))
      }
      this.monthMenuOpen = false
      this.dateMenuOpen = ''
      this.dateFieldMenuOpen = ''
      this.$nextTick(this.updateDateRangePanelPosition)
    },
    selectDateFieldValue(type, field, value) {
      this.dateDraft[type][field] = value
      const maxDay = getDayCount(this.dateDraft[type].year, this.dateDraft[type].month)
      if (this.dateDraft[type].day > maxDay) {
        this.dateDraft[type].day = maxDay
      }
      this.dateFieldMenuOpen = ''
    },
    formatDraftDisplay(type) {
      return formatDateLabel(formatDraftDate(this.dateDraft[type]))
    },
    updateDateRangePanelPosition() {
      if (!this.showDateRangePanel || !this.$refs.dateRangeTrigger) {
        return
      }
      const triggerRect = this.$refs.dateRangeTrigger.getBoundingClientRect()
      const panel = this.$el.querySelector('.date-range-panel')
      const panelWidth = panel ? panel.getBoundingClientRect().width : Math.min(620, Math.max(420, window.innerWidth * 0.27))
      const gap = window.innerHeight * 0.0045
      const left = Math.max(8, Math.min(triggerRect.right - panelWidth, window.innerWidth - panelWidth - 8))
      this.dateRangePanelStyle = {
        top: `${triggerRect.bottom + gap}px`,
        left: `${left}px`
      }
    },
    closeStatisticsDialog() {
      this.showStatsDialog = false
    },
    openFloatingPage() {
      this.$router.push('/notice')
    },
    closeFloatingNotice() {
      this.showFloatingNotice = false
      window.cancelAnimationFrame(this.floatingFrameId)
    },
    setFloatingNoticePaused(paused) {
      if (this.floatingMotion) {
        this.floatingMotion.paused = paused
      }
    },
    startFloatingNotice() {
      const element = this.$refs.floatingNotice
      if (!element) return

      const width = element.offsetWidth
      const height = element.offsetHeight
      const margin = 14
      const topBoundary = Math.max(68, window.innerHeight * 0.08)
      const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      const startX = Math.max(margin, Math.min(window.innerWidth * 0.18, window.innerWidth - width - margin))
      const startY = Math.max(topBoundary, Math.min(window.innerHeight * 0.24, window.innerHeight - height - margin))
      this.floatingMotion = {
        x: startX,
        y: startY,
        vx: 105,
        vy: 71.25,
        lastTime: window.performance.now(),
        paused: false,
        reducedMotion
      }
      element.style.transform = `translate3d(${startX}px, ${startY}px, 0)`
      if (!reducedMotion) {
        this.floatingFrameId = window.requestAnimationFrame(this.moveFloatingNotice)
      }
    },
    moveFloatingNotice(timestamp) {
      const element = this.$refs.floatingNotice
      const motion = this.floatingMotion
      if (!element || !motion || !this.showFloatingNotice) return

      const elapsed = Math.min((timestamp - motion.lastTime) / 1000, 0.05)
      motion.lastTime = timestamp
      if (!motion.paused) {
        motion.x += motion.vx * elapsed
        motion.y += motion.vy * elapsed

        const margin = 14
        const topBoundary = Math.max(68, window.innerHeight * 0.08)
        const maxX = Math.max(margin, window.innerWidth - element.offsetWidth - margin)
        const maxY = Math.max(topBoundary, window.innerHeight - element.offsetHeight - margin)

        if (motion.x <= margin || motion.x >= maxX) {
          motion.x = Math.max(margin, Math.min(motion.x, maxX))
          motion.vx *= -1
        }
        if (motion.y <= topBoundary || motion.y >= maxY) {
          motion.y = Math.max(topBoundary, Math.min(motion.y, maxY))
          motion.vy *= -1
        }
        element.style.transform = `translate3d(${motion.x}px, ${motion.y}px, 0)`
      }
      this.floatingFrameId = window.requestAnimationFrame(this.moveFloatingNotice)
    },
    handleLogout() {
      const confirmed = window.confirm('确定退出当前前台账号吗？')
      if (!confirmed) {
        return
      }

      clearAuthState()
      this.$router.replace('/login')
    },
    async openAdmin() {
      let adminUrl = ''
      try {
        adminUrl = await getAdminUrl()
      } catch (error) {
        console.error('[datav-admin-url] 后台地址获取失败', error)
        window.alert(error.message || '后台地址获取失败')
        return
      }

      console.info('[datav-admin-url] 点击后台按钮获取到的地址', adminUrl)
      if (!adminUrl) {
        console.warn('[datav-admin-url] 后台地址为空，弹出未配置提示')
        window.alert('后台地址未配置')
        return
      }

      console.info('[datav-admin-url] 新窗口跳转后台地址', adminUrl)
      window.open(adminUrl, '_blank', 'noopener,noreferrer')
    }
  }
}
</script>

<style scoped>
.dashboard-screen {
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background-color: #06193b;
}

.screen-shell {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #06193b;
  background-position: center top;
  background-repeat: no-repeat;
  background-size: 100% 100%;
}

.top-banner {
  position: absolute;
  top: -0.2vh;
  left: -1.5%;
  width: 103%;
  height: auto;
  z-index: 1;
  pointer-events: none;
}

.screen-header {
  position: relative;
  z-index: 6;
  flex-shrink: 0;
  height: 8.8vh;
}

.header-meta {
  position: absolute;
  top: 5vh;
  left: 1vw;
  display: flex;
  align-items: center;
  gap: 1.2vw;
  color: #d5edff;
  font-size: 0.75vw;
  letter-spacing: 0.05vw;
}

.header-title {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  height: 100%;
  padding-top: 0.5vh;
  color: #ffffff;
  font-size: 1.4vw;
  font-weight: 700;
  letter-spacing: 0.3vw;
  text-shadow: 0 0.4vh 1.6vh rgba(66, 170, 255, 0.45);
}

.header-actions {
  position: fixed;
  top: 5vh;
  right: 1vw;
  display: flex;
  align-items: center;
  gap: 0.5vw;
  z-index: 7;
}

.header-actions button {
  height: 1.9vw;
  padding: 0 0.7vw;
  border: 1px solid rgba(72, 201, 255, 0.4);
  border-radius: 0.3vw;
  color: #dff7ff;
  font-size: 0.7vw;
  line-height: 1;
  background: linear-gradient(180deg, rgba(7, 93, 211, 0.9), rgba(4, 58, 122, 0.7));
  box-shadow: inset 0 0 0.5vw rgba(91, 228, 255, 0.25);
  cursor: pointer;
}

.date-range-filter {
  position: relative;
  z-index: 1;
}

.header-actions .date-range-trigger {
  width: 7.4vw;
  min-width: 7.4vw;
  overflow: hidden;
  padding-left: 0.85vw;
  padding-right: 0.85vw;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.header-actions .date-range-trigger.active {
  border-color: rgba(112, 226, 255, 0.82);
  color: #ffffff;
  background: linear-gradient(180deg, rgba(16, 132, 239, 0.96), rgba(6, 80, 164, 0.82));
}

.date-range-panel {
  position: fixed;
  z-index: 999930;
  display: grid;
  grid-template-columns: 8.2vw minmax(0, 1fr);
  grid-template-rows: auto auto;
  column-gap: 0.86vw;
  width: clamp(420px, 27vw, 620px);
  padding: 0.72vw;
  border: 1px solid rgba(84, 197, 255, 0.42);
  border-radius: 0.56vw;
  box-sizing: border-box;
  background: linear-gradient(180deg, rgba(7, 56, 132, 0.98), rgba(4, 30, 83, 0.98));
  box-shadow: 0 0.7vw 1.4vw rgba(0, 0, 0, 0.32), inset 0 0 1vw rgba(83, 211, 255, 0.12);
}

.date-range-panel.is-expanded {
  width: clamp(420px, 27vw, 620px);
  padding-bottom: 0.82vw;
}

.date-range-panel.is-month-expanded {
  width: clamp(420px, 27vw, 620px);
}

.date-range-quick-tools {
  position: relative;
  z-index: 2;
  grid-column: 1;
  grid-row: 1 / span 2;
  display: flex;
  flex-direction: column;
  gap: 0.38vw;
  min-width: 0;
  padding-right: 0.86vw;
  border-right: 1px solid rgba(77, 163, 222, 0.34);
}

.date-range-month-control {
  position: relative;
}

.date-range-month-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 1.9vw;
  padding: 0 0.56vw;
  border: 0.05vw solid rgba(102, 217, 255, 0.48);
  border-radius: 0.34vw;
  color: #effaff;
  font-size: 0.74vw;
  text-align: left;
  background: linear-gradient(180deg, rgba(18, 120, 231, 0.68), rgba(5, 66, 159, 0.62));
  box-shadow: none;
  cursor: pointer;
}

.date-range-month-trigger.active,
.date-range-month-trigger:hover {
  border-color: rgba(139, 230, 255, 0.86);
  background: linear-gradient(180deg, rgba(29, 145, 244, 0.9), rgba(8, 77, 171, 0.82));
}

.date-range-month-arrow {
  width: 0.42vw;
  height: 0.42vw;
  border-right: 0.1vw solid rgba(236, 249, 255, 0.94);
  border-bottom: 0.1vw solid rgba(236, 249, 255, 0.94);
  transform: rotate(45deg) translateY(-0.12vw);
  transition: transform 0.2s ease;
}

.date-range-month-arrow.is-open {
  transform: rotate(225deg) translate(-0.08vw, -0.02vw);
}

.date-range-month-menu {
  position: absolute;
  top: calc(100% + 0.32vw);
  left: 0;
  right: 0;
  z-index: 999960;
  max-height: 10.8vw;
  padding: 0.18vw;
  border: 0.05vw solid rgba(84, 197, 255, 0.52);
  border-radius: 0.34vw;
  background: linear-gradient(180deg, rgba(7, 56, 132, 0.99), rgba(4, 30, 83, 0.99));
  box-shadow: 0 0.5vw 1vw rgba(0, 0, 0, 0.26);
  overflow-y: auto;
}

.date-range-month-option {
  display: block;
  width: 100%;
  min-height: 1.72vw;
  padding: 0.18vw 0.24vw;
  border: 0;
  border-radius: 0.24vw;
  color: rgba(231, 247, 255, 0.9);
  font-size: 0.72vw;
  text-align: left;
  background: transparent;
  box-shadow: none;
  cursor: pointer;
}

.date-range-month-option:hover,
.date-range-month-option.active {
  color: #ffffff;
  background: linear-gradient(180deg, rgba(36, 147, 255, 0.96), rgba(23, 96, 219, 0.9));
}

.date-range-month-menu,
.date-picker-select-menu {
  scrollbar-width: thin;
  scrollbar-color: #408ac5 #092e61;
}

.date-range-month-menu::-webkit-scrollbar,
.date-picker-select-menu::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.date-range-month-menu::-webkit-scrollbar-track,
.date-picker-select-menu::-webkit-scrollbar-track {
  border-radius: 6px;
  background: #092e61;
}

.date-range-month-menu::-webkit-scrollbar-thumb,
.date-picker-select-menu::-webkit-scrollbar-thumb {
  border: 1px solid #092e61;
  border-radius: 6px;
  background: #408ac5;
}

.date-range-month-menu::-webkit-scrollbar-thumb:hover,
.date-picker-select-menu::-webkit-scrollbar-thumb:hover {
  background: #68bbeb;
}

.date-range-month-menu::-webkit-scrollbar-button,
.date-picker-select-menu::-webkit-scrollbar-button {
  display: none;
  width: 0;
  height: 0;
}

.date-range-shortcuts {
  display: flex;
  flex-direction: column;
  gap: 0.08vw;
}

.date-range-shortcut {
  width: 100%;
  min-height: 1.72vw;
  padding: 0.14vw 0.24vw;
  border: 0;
  border-radius: 0.24vw;
  color: #c8e7ff;
  font-size: 0.72vw;
  text-align: left;
  background: transparent;
  box-shadow: none;
  cursor: pointer;
}

.date-range-shortcut:hover {
  color: #ffffff;
  background: rgba(18, 90, 160, 0.72);
}

.date-range-toolbar {
  grid-column: 2;
  grid-row: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: start;
  gap: 0.42vw;
  min-width: 0;
}

.date-range-date-control {
  position: relative;
  min-width: 0;
}

.date-range-date-trigger {
  width: 100%;
  height: 1.9vw;
  padding: 0 0.56vw;
  border: 0;
  border-radius: 0.48vw;
  color: #effaff;
  font-size: 0.74vw;
  text-align: center;
  background: linear-gradient(180deg, rgba(18, 120, 231, 0.88), rgba(5, 66, 159, 0.82));
  box-shadow: inset 0 0 0.58vw rgba(122, 230, 255, 0.16);
}

.date-range-sep {
  height: 1.9vw;
  color: rgba(234, 245, 255, 0.92);
  font-size: 1vw;
  line-height: 1.9vw;
  text-align: center;
}

.date-picker-panel {
  position: relative;
  z-index: 999940;
  width: calc(18.1vw - 1.44vw);
  margin-top: 0.5vw;
  padding: 0.72vw;
  border: 0.05vw solid rgba(84, 197, 255, 0.42);
  border-radius: 0.56vw;
  box-sizing: border-box;
  background: linear-gradient(180deg, rgba(7, 56, 132, 0.98), rgba(4, 30, 83, 0.98));
  box-shadow: 0 0.6vw 1.2vw rgba(0, 0, 0, 0.24);
}

.date-picker-panel--right {
  transform: translateX(calc(-50% - 0.21vw));
}

.date-picker-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.42vw;
}

.date-picker-field {
  display: flex;
  flex-direction: column;
  gap: 0.24vw;
  color: #dff4ff;
  font-size: 0.66vw;
}

.date-picker-select {
  position: relative;
}

.date-picker-select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 1.9vw;
  padding: 0 0.42vw;
  border: 0.05vw solid rgba(102, 217, 255, 0.28);
  border-radius: 0.34vw;
  color: #effaff;
  font-size: 0.72vw;
  background: linear-gradient(180deg, rgba(18, 120, 231, 0.68), rgba(5, 66, 159, 0.62));
  box-shadow: none;
  cursor: pointer;
}

.date-picker-select-arrow {
  width: 0.34vw;
  height: 0.22vw;
  background: rgba(236, 249, 255, 0.92);
  clip-path: polygon(0 0, 100% 0, 50% 100%);
  transition: transform 0.2s ease;
}

.date-picker-select-arrow.is-open {
  transform: rotate(180deg);
}

.date-picker-select-menu {
  position: absolute;
  top: calc(100% + 0.18vw);
  left: 0;
  right: 0;
  z-index: 999950;
  max-height: 10.8vw;
  padding: 0.18vw;
  border: 0.05vw solid rgba(84, 197, 255, 0.42);
  border-radius: 0.34vw;
  background: linear-gradient(180deg, rgba(7, 56, 132, 0.98), rgba(4, 30, 83, 0.98));
  box-shadow: 0 0.5vw 1vw rgba(0, 0, 0, 0.22);
  overflow-y: auto;
}

.date-picker-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.42vw;
  margin-top: 0.72vw;
}

.date-picker-action {
  min-width: 3.2vw;
  height: 1.7vw;
  padding: 0 0.54vw;
  border: 0;
  border-radius: 0.34vw;
  color: #f3fcff;
  font-size: 0.7vw;
  background: linear-gradient(180deg, rgba(44, 189, 255, 0.95), rgba(10, 108, 224, 0.86));
  box-shadow: none;
}

.date-picker-action--ghost {
  background: linear-gradient(180deg, rgba(25, 95, 176, 0.95), rgba(8, 61, 128, 0.86));
}

.date-picker-select-option {
  width: 100%;
  min-height: 1.58vw;
  height: auto;
  padding: 0.18vw 0.2vw;
  border: 0;
  border-radius: 0.24vw;
  color: rgba(231, 247, 255, 0.9);
  font-size: 0.7vw;
  text-align: center;
  background: transparent;
  box-shadow: none;
  cursor: pointer;
}

.date-picker-select-option:hover,
.date-picker-select-option.active {
  color: #ffffff;
  background: linear-gradient(180deg, rgba(36, 147, 255, 0.96), rgba(23, 96, 219, 0.9));
}

.date-range-actions {
  grid-column: 2;
  grid-row: 2;
  display: flex;
  justify-content: flex-end;
  gap: 0.42vw;
  margin-top: 0.72vw;
}

.date-range-action {
  min-width: 3.2vw;
  height: 1.7vw;
  padding: 0 0.54vw;
  border: 0;
  border-radius: 0.34vw;
  color: #f3fcff;
  font-size: 0.7vw;
  background: linear-gradient(180deg, rgba(44, 189, 255, 0.95), rgba(10, 108, 224, 0.86));
  box-shadow: none;
}

.date-range-action--ghost {
  background: linear-gradient(180deg, rgba(25, 95, 176, 0.95), rgba(8, 61, 128, 0.86));
}

@media (max-width: 700px) {
  .date-range-panel,
  .date-range-panel.is-expanded,
  .date-range-panel.is-month-expanded {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto auto auto;
    width: calc(100vw - 16px);
    max-width: calc(100vw - 16px);
    column-gap: 0;
  }

  .date-range-quick-tools {
    grid-column: 1;
    grid-row: 1;
    padding-right: 0;
    padding-bottom: 0.55vw;
    border-right: 0;
    border-bottom: 1px solid rgba(77, 163, 222, 0.34);
  }

  .date-range-toolbar {
    grid-column: 1;
    grid-row: 2;
  }

  .date-range-actions {
    grid-column: 1;
    grid-row: 3;
  }
}

.screen-body {
  position: relative;
  flex: 1;
  display: flex;
  gap: 0.48vw;
  padding: 0.3vh 0.56vw 1.9vh 0.34vw;
  box-sizing: border-box;
  min-height: 0;
}

.body-panel {
  min-width: 0;
  min-height: 0;
}

.floating-notice {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 30;
  width: clamp(176px, 11vw, 224px);
  min-height: clamp(84px, 9vh, 110px);
  border: 1px solid rgba(96, 222, 255, 0.62);
  border-radius: 8px;
  background:
    linear-gradient(135deg, rgba(13, 132, 220, 0.96), rgba(6, 56, 139, 0.96)),
    #0a4a9b;
  box-shadow: 0 0.7vw 1.8vw rgba(0, 15, 52, 0.42), inset 0 0 1vw rgba(124, 232, 255, 0.16);
  will-change: transform;
}

.floating-notice__link {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  min-height: inherit;
  padding: 14px 38px 14px 16px;
  border: 0;
  color: #ffffff;
  text-align: left;
  background: transparent;
  cursor: pointer;
}

.floating-notice__link span {
  color: #83e8ff;
  font-size: clamp(11px, 0.62vw, 13px);
}

.floating-notice__link strong {
  margin-top: 5px;
  font-size: clamp(14px, 0.82vw, 17px);
  line-height: 1.25;
}

.floating-notice__close {
  position: absolute;
  top: 7px;
  right: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 25px;
  height: 25px;
  padding: 0;
  border: 1px solid rgba(180, 240, 255, 0.34);
  border-radius: 50%;
  color: #dff9ff;
  font-size: 18px;
  line-height: 1;
  background: rgba(1, 37, 99, 0.58);
  cursor: pointer;
}

.left-col {
  flex: 0 0 26.2%;
}

.center-col {
  flex: 1 1 46%;
}

.right-col {
  flex: 0 0 25.8%;
}
</style>
